import React from 'react';
import { useTranslation } from 'react-i18next';
import '../pages/DeliverySlipPage.css';

/**
 * DeliverySlipView — Compact print view designed for delivery drivers (domiciliarios).
 * Shows only essential order information: client, address, products, total, delivery time.
 * Optimized for minimal paper usage on thermal printers (58mm/80mm).
 */
const DeliverySlipView = ({ sale }) => {
    const { t, i18n } = useTranslation();
    const { language } = i18n;

    if (!sale) return null;

    const formatCurrency = (num) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num || 0);
    };

    const totalQuantity = sale.products.reduce((acc, item) => acc + item.quantity, 0);
    const remaining = parseFloat(sale.total_amount) - parseFloat(sale.paid_amount);
    const isPending = remaining > 0;

    // Cash received / change — shown only when the customer overpaid in cash.
    const cashReceived = sale.cash_received != null ? parseFloat(sale.cash_received) : null;
    const changeDue = cashReceived != null && cashReceived > parseFloat(sale.total_amount)
        ? cashReceived - parseFloat(sale.total_amount)
        : null;

    const formatDeliveryDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString(language, {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="slip-container">
            {/* ── Header ── */}
            <div className="slip-header">
                <div className="slip-title">
                    {t('deliverySlip.title', 'PEDIDO')} #{sale.invoice_number}
                </div>
                {sale.business_name && (
                    <div className="slip-business">{sale.business_name}</div>
                )}
            </div>

            <div className="slip-divider"></div>

            {/* ── Client Info ── */}
            <div className="slip-section">
                {sale.client_name && (
                    <div className="slip-row">
                        <span className="slip-label">{t('deliverySlip.client', 'Cliente')}:</span>
                        <span className="slip-value slip-value--bold">{sale.client_name}</span>
                    </div>
                )}
                {sale.client_address && (
                    <div className="slip-row slip-row--address">
                        <span className="slip-label">{t('deliverySlip.address', 'Dir')}:</span>
                        <span className="slip-value slip-value--bold slip-value--address">{sale.client_address}</span>
                    </div>
                )}
                {sale.client_phone && (
                    <div className="slip-row">
                        <span className="slip-label">{t('deliverySlip.phone', 'Tel')}:</span>
                        <span className="slip-value">{sale.client_phone}</span>
                    </div>
                )}
            </div>

            <div className="slip-divider slip-divider--dashed"></div>

            {/* ── Delivery Date ── */}
            {sale.delivery_datetime && (
                <>
                    <div className="slip-section">
                        <div className="slip-row">
                            <span className="slip-label">{t('deliverySlip.deliveryDate', 'Entrega')}:</span>
                            <span className="slip-value slip-value--bold">{formatDeliveryDate(sale.delivery_datetime)}</span>
                        </div>
                    </div>
                    <div className="slip-divider slip-divider--dashed"></div>
                </>
            )}

            {/* ── Products ── */}
            <div className="slip-section">
                <div className="slip-items-header">
                    {totalQuantity} {t('deliverySlip.items', 'artículo(s)')}
                </div>
                {sale.products.map((item, idx) => (
                    <div className="slip-item" key={item.item_id || idx}>
                        <span className="slip-item-qty">{item.quantity}x</span>
                        <span className="slip-item-name">{item.product_name}</span>
                        <span className="slip-item-price">{formatCurrency(item.quantity * item.unit_price)}</span>
                    </div>
                ))}
            </div>

            <div className="slip-divider"></div>

            {/* ── Total ── */}
            <div className="slip-section">
                <div className="slip-total">
                    <span className="slip-total-label">{t('deliverySlip.total', 'TOTAL')}:</span>
                    <span className="slip-total-value">{formatCurrency(sale.total_amount)}</span>
                </div>

                {/* Payment status — critical for delivery driver to know if they need to collect */}
                {isPending ? (
                    <div className="slip-collect">
                        <span className="slip-collect-label">{t('deliverySlip.toCollect', 'COBRAR')}:</span>
                        <span className="slip-collect-value">{formatCurrency(remaining)}</span>
                    </div>
                ) : (
                    <div className="slip-paid-badge">
                        ✓ {t('deliverySlip.paid', 'PAGADO')}
                    </div>
                )}

                {/* Cash received & change — helps the driver hand back the right change */}
                {changeDue != null && (
                    <>
                        <div className="slip-row">
                            <span className="slip-label">{t('deliverySlip.received', 'Recibido')}:</span>
                            <span className="slip-value">{formatCurrency(cashReceived)}</span>
                        </div>
                        <div className="slip-row">
                            <span className="slip-label">{t('deliverySlip.change', 'Vuelto')}:</span>
                            <span className="slip-value slip-value--bold">{formatCurrency(changeDue)}</span>
                        </div>
                    </>
                )}
            </div>

            {/* ── Observations ── */}
            {sale.observations && (
                <>
                    <div className="slip-divider slip-divider--dashed"></div>
                    <div className="slip-section">
                        <div className="slip-observations">
                            <strong>{t('deliverySlip.notes', 'Nota')}:</strong> {sale.observations}
                        </div>
                    </div>
                </>
            )}

            {/* ── Footer ── */}
            <div className="slip-footer">
                <div className="slip-footer-date">
                    {new Date().toLocaleDateString(language, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </div>
            </div>
        </div>
    );
};

export default DeliverySlipView;
